import { makeAutoObservable } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import {
  ReportPeriod,
  IncomeStatement,
  BalanceSheet,
  CashFlowStatement,
  JobProfitability,
  CustomerProfitability,
  JobCostVariance,
  ARAgingReport,
  APAgingReport,
  CashFlowForecast,
  ExecutiveKPIs,
  AgingBucket,
  Job,
  CostAllocation,
  Invoice,
  Bill,
  Payment,
  ProductionCost,
  LaborEntry,
  EquipmentCost,
} from "../../types";

class FinancialReportsStore {
  rootStore: RootStore;

  // Observables
  selectedReportType:
    | "income-statement"
    | "balance-sheet"
    | "cash-flow"
    | "job-profitability"
    | "customer-profitability"
    | "job-variance"
    | "ar-aging"
    | "ap-aging"
    | "cash-forecast"
    | "executive-kpi" = "income-statement";

  selectedPeriod: ReportPeriod = ReportPeriod.MONTHLY;
  startDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString()
    .split("T")[0];
  endDate = new Date().toISOString().split("T")[0];
  compareWithPreviousPeriod = false;

  // Plant Configuration
  showPlantColumn = true;
  subtotalByPlant = true;
  selectedPlants: string[] = []; // Empty = all plants

  // Available Plants (mock - would come from API)
  availablePlants = [
    { id: "plant-1", name: "Apex Main", code: "AX" },
    { id: "plant-2", name: "Northpoint", code: "NP" },
    { id: "plant-3", name: "Sierra Valley", code: "SV" },
    { id: "plant-4", name: "Summit", code: "SMT" },
    { id: "plant-5", name: "Harbor", code: "HBR" },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this, {
      rootStore: false,
    });
  }

  // Actions
  setSelectedReportType = (
    reportType:
      | "income-statement"
      | "balance-sheet"
      | "cash-flow"
      | "job-profitability"
      | "customer-profitability"
      | "job-variance"
      | "ar-aging"
      | "ap-aging"
      | "cash-forecast"
      | "executive-kpi"
  ) => {
    this.selectedReportType = reportType;
  };

  setSelectedPeriod = (period: ReportPeriod) => {
    this.selectedPeriod = period;
    this.updateDatesForPeriod(period);
  };

  setStartDate = (date: string) => {
    this.startDate = date;
  };

  setEndDate = (date: string) => {
    this.endDate = date;
  };

  setCompareWithPreviousPeriod = (compare: boolean) => {
    this.compareWithPreviousPeriod = compare;
  };

  // Plant Filter Actions
  setShowPlantColumn = (show: boolean) => {
    this.showPlantColumn = show;
  };

  setSubtotalByPlant = (subtotal: boolean) => {
    this.subtotalByPlant = subtotal;
  };

  setSelectedPlants = (plantIds: string[]) => {
    this.selectedPlants = plantIds;
  };

  togglePlantSelection = (plantId: string) => {
    if (this.selectedPlants.includes(plantId)) {
      this.selectedPlants = this.selectedPlants.filter((id) => id !== plantId);
    } else {
      this.selectedPlants = [...this.selectedPlants, plantId];
    }
  };

  selectAllPlants = () => {
    this.selectedPlants = this.availablePlants.map((p) => p.id);
  };

  clearPlantSelection = () => {
    this.selectedPlants = [];
  };

  /**
   * Get plant name by ID
   */
  getPlantName = (plantId: string): string => {
    return this.availablePlants.find((p) => p.id === plantId)?.name || plantId;
  };

  /**
   * Get plant code by ID
   */
  getPlantCode = (plantId: string): string => {
    return this.availablePlants.find((p) => p.id === plantId)?.code || plantId;
  };

  /**
   * Check if filtering by specific plants
   */
  get isFilteringByPlant(): boolean {
    return this.selectedPlants.length > 0;
  }

  /**
   * Get plants to use for filtering (all if none selected)
   */
  get effectivePlants(): string[] {
    return this.selectedPlants.length > 0
      ? this.selectedPlants
      : this.availablePlants.map((p) => p.id);
  }

  private updateDatesForPeriod = (period: ReportPeriod) => {
    const now = new Date();
    let start: Date;
    const end: Date = now;

    switch (period) {
      case ReportPeriod.DAILY:
        start = new Date(now);
        break;
      case ReportPeriod.WEEKLY:
        start = new Date(now);
        start.setDate(now.getDate() - 7);
        break;
      case ReportPeriod.MONTHLY:
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case ReportPeriod.QUARTERLY:
        const quarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case ReportPeriod.YEARLY:
        start = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), 1);
    }

    this.startDate = start.toISOString().split("T")[0];
    this.endDate = end.toISOString().split("T")[0];
  };

  // Computed - Service Stores
  get invoicesStore() {
    return this.rootStore.invoicesStore as ServiceStore;
  }

  get billsStore() {
    return this.rootStore.billsStore as ServiceStore;
  }

  get paymentsStore() {
    return this.rootStore.paymentsStore as ServiceStore;
  }

  get jobStore() {
    return this.rootStore.jobStore as ServiceStore;
  }

  get costAllocationsStore() {
    return this.rootStore.costAllocationsStore as ServiceStore;
  }

  get productionCostsStore() {
    return this.rootStore.productionCostsStore as ServiceStore;
  }

  get laborEntriesStore() {
    return this.rootStore.laborEntriesStore as ServiceStore;
  }

  get equipmentCostsStore() {
    return this.rootStore.equipmentCostsStore as ServiceStore;
  }

  get chartOfAccountsStore() {
    return this.rootStore.chartOfAccountsStore as ServiceStore;
  }

  // Computed - Entities
  get invoices(): Invoice[] {
    return (this.invoicesStore?.entities as unknown as Invoice[]) || [];
  }

  get bills(): Bill[] {
    return (this.billsStore?.entities as unknown as Bill[]) || [];
  }

  get payments(): Payment[] {
    return (this.paymentsStore?.entities as unknown as Payment[]) || [];
  }

  get jobs(): Job[] {
    return (this.jobStore?.entities as unknown as Job[]) || [];
  }

  get costAllocations(): CostAllocation[] {
    return (
      (this.costAllocationsStore?.entities as unknown as CostAllocation[]) || []
    );
  }

  get productionCosts(): ProductionCost[] {
    return (
      (this.productionCostsStore?.entities as unknown as ProductionCost[]) || []
    );
  }

  get laborEntries(): LaborEntry[] {
    return (
      (this.laborEntriesStore?.entities as unknown as LaborEntry[]) || []
    );
  }

  get equipmentCosts(): EquipmentCost[] {
    return (
      (this.equipmentCostsStore?.entities as unknown as EquipmentCost[]) || []
    );
  }

  // Helper: Filter by date range
  private isInDateRange = (date: string): boolean => {
    return date >= this.startDate && date <= this.endDate;
  };

  // ============================================================================
  // FINANCIAL STATEMENTS
  // ============================================================================

  get incomeStatement(): IncomeStatement {
    // Filter invoices and bills in the date range
    const periodInvoices = this.invoices.filter((inv) =>
      this.isInDateRange(inv.invoiceDate)
    );
    const periodBills = this.bills.filter((bill) =>
      this.isInDateRange(bill.billDate)
    );

    // Real data calculation
    const sales = periodInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
    const otherIncome = 0;
    const totalRevenue = sales + otherIncome;

    const directMaterials = this.productionCosts
      .filter(
        (pc) =>
          this.isInDateRange(pc.costDate) &&
          (pc.category === "direct_material" || pc.category === "indirect_material")
      )
      .reduce((sum, pc) => sum + pc.totalCost, 0);

    const directLabor = this.laborEntries
      .filter((le) => this.isInDateRange(le.workDate))
      .reduce((sum, le) => sum + le.totalLabor, 0);

    const manufacturingOverhead = this.productionCosts
      .filter(
        (pc) =>
          this.isInDateRange(pc.costDate) &&
          pc.category === "manufacturing_overhead"
      )
      .reduce((sum, pc) => sum + pc.totalCost, 0);

    const totalCOGS = directMaterials + directLabor + manufacturingOverhead;

    const grossProfit = totalRevenue - totalCOGS;
    const grossProfitMargin =
      totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;

    const selling = periodBills
      .filter((bill) => bill.notes?.toLowerCase().includes("selling"))
      .reduce((sum, bill) => sum + bill.totalAmount, 0);

    const administrative = periodBills
      .filter((bill) => bill.notes?.toLowerCase().includes("admin"))
      .reduce((sum, bill) => sum + bill.totalAmount, 0);

    const depreciation = this.equipmentCosts
      .filter(
        (ec) =>
          this.isInDateRange(ec.usageDate) && ec.costType === "depreciation"
      )
      .reduce((sum, ec) => sum + ec.totalCost, 0);

    const otherExpenses = periodBills
      .filter(
        (bill) =>
          !bill.notes?.toLowerCase().includes("selling") &&
          !bill.notes?.toLowerCase().includes("admin")
      )
      .reduce((sum, bill) => sum + bill.totalAmount, 0);

    const totalOperatingExpenses =
      selling + administrative + depreciation + otherExpenses;

    const operatingIncome = grossProfit - totalOperatingExpenses;
    const operatingMargin =
      totalRevenue > 0 ? (operatingIncome / totalRevenue) * 100 : 0;

    const netIncome = operatingIncome;
    const netProfitMargin =
      totalRevenue > 0 ? (netIncome / totalRevenue) * 100 : 0;

    return {
      id: `income-statement-${this.startDate}-${this.endDate}`,
      period: this.selectedPeriod,
      startDate: this.startDate,
      endDate: this.endDate,
      revenue: {
        sales,
        otherIncome,
        totalRevenue,
      },
      costOfGoodsSold: {
        directMaterials,
        directLabor,
        manufacturingOverhead,
        totalCOGS,
      },
      grossProfit,
      grossProfitMargin,
      operatingExpenses: {
        selling,
        administrative,
        depreciation,
        other: otherExpenses,
        totalOperatingExpenses,
      },
      operatingIncome,
      operatingMargin,
      otherExpenses: 0,
      netIncome,
      netProfitMargin,
    };
  }

  get balanceSheet(): BalanceSheet {
    // Calculate as of end date
    const asOfInvoices = this.invoices.filter((inv) => inv.invoiceDate <= this.endDate);
    const asOfBills = this.bills.filter((bill) => bill.billDate <= this.endDate);
    const asOfPayments = this.payments.filter(
      (pmt) => pmt.paymentDate <= this.endDate
    );

    // Assets
    const accountsReceivable = asOfInvoices.reduce(
      (sum, inv) => sum + inv.amountDue,
      0
    );
    const cash = asOfPayments.reduce((sum, pmt) => {
      return pmt.paymentType === "customer_payment"
        ? sum + pmt.amount
        : sum - pmt.amount;
    }, 0);
    const inventory = 0; // Placeholder - would come from inventory module
    const prepaidExpenses = 0; // Placeholder
    const totalCurrentAssets = cash + accountsReceivable + inventory + prepaidExpenses;

    // Fixed assets
    const propertyPlantEquipment = 0; // Placeholder
    const accumulatedDepreciation = this.equipmentCosts
      .filter(
        (ec) => ec.usageDate <= this.endDate && ec.costType === "depreciation"
      )
      .reduce((sum, ec) => sum + ec.totalCost, 0);
    const netFixedAssets = propertyPlantEquipment - accumulatedDepreciation;

    const totalAssets = totalCurrentAssets + netFixedAssets;

    // Liabilities
    const accountsPayable = asOfBills.reduce(
      (sum, bill) => sum + (bill.totalAmount - 0),
      0
    ); // Assuming totalAmount = amountDue
    const accruedExpenses = 0; // Placeholder
    const currentPortionLongTermDebt = 0; // Placeholder
    const totalCurrentLiabilities =
      accountsPayable + accruedExpenses + currentPortionLongTermDebt;

    const longTermDebt = 0; // Placeholder
    const otherLiabilities = 0; // Placeholder
    const totalLongTermLiabilities = longTermDebt + otherLiabilities;

    const totalLiabilities = totalCurrentLiabilities + totalLongTermLiabilities;

    // Equity
    const commonStock = 0; // Placeholder
    const retainedEarnings = totalAssets - totalLiabilities - commonStock;
    const totalEquity = commonStock + retainedEarnings;

    return {
      id: `balance-sheet-${this.endDate}`,
      asOfDate: this.endDate,
      assets: {
        currentAssets: {
          cash,
          accountsReceivable,
          inventory,
          prepaidExpenses,
          totalCurrentAssets,
        },
        fixedAssets: {
          propertyPlantEquipment,
          accumulatedDepreciation,
          netFixedAssets,
        },
        totalAssets,
      },
      liabilities: {
        currentLiabilities: {
          accountsPayable,
          accruedExpenses,
          currentPortionLongTermDebt,
          totalCurrentLiabilities,
        },
        longTermLiabilities: {
          longTermDebt,
          otherLiabilities,
          totalLongTermLiabilities,
        },
        totalLiabilities,
      },
      equity: {
        commonStock,
        retainedEarnings,
        totalEquity,
      },
      totalLiabilitiesAndEquity: totalLiabilities + totalEquity,
    };
  }

  get cashFlowStatement(): CashFlowStatement {
    const netIncome = this.incomeStatement.netIncome;

    // Operating activities
    const depreciation = this.incomeStatement.operatingExpenses.depreciation;

    // Calculate changes (would need beginning balances in real impl)
    const accountsReceivableChange = 0; // Placeholder
    const inventoryChange = 0; // Placeholder
    const accountsPayableChange = 0; // Placeholder

    const netCashFromOperations =
      netIncome +
      depreciation +
      accountsReceivableChange +
      inventoryChange +
      accountsPayableChange;

    // Investing activities
    const equipmentPurchases = 0; // Placeholder
    const assetSales = 0; // Placeholder
    const netCashFromInvesting = assetSales - equipmentPurchases;

    // Financing activities
    const debtProceeds = 0; // Placeholder
    const debtRepayments = 0; // Placeholder
    const dividendsPaid = 0; // Placeholder
    const netCashFromFinancing = debtProceeds - debtRepayments - dividendsPaid;

    const netCashChange =
      netCashFromOperations + netCashFromInvesting + netCashFromFinancing;

    const beginningCash = 0; // Placeholder
    const endingCash = beginningCash + netCashChange;

    return {
      id: `cash-flow-${this.startDate}-${this.endDate}`,
      period: this.selectedPeriod,
      startDate: this.startDate,
      endDate: this.endDate,
      operatingActivities: {
        netIncome,
        depreciation,
        accountsReceivableChange,
        inventoryChange,
        accountsPayableChange,
        netCashFromOperations,
      },
      investingActivities: {
        equipmentPurchases,
        assetSales,
        netCashFromInvesting,
      },
      financingActivities: {
        debtProceeds,
        debtRepayments,
        dividendsPaid,
        netCashFromFinancing,
      },
      netCashChange,
      beginningCash,
      endingCash,
    };
  }

  // ============================================================================
  // PROFITABILITY REPORTS
  // ============================================================================

  get jobProfitabilityReport(): JobProfitability[] {
    return this.jobs.map((job) => {
      // Find invoices for this job (would need jobId in invoice in real impl)
      const revenue = 0; // Placeholder - would match by job

      const totalCost = job.actualCost || 0;
      const grossProfit = revenue - totalCost;
      const grossProfitMargin = revenue > 0 ? (grossProfit / revenue) * 100 : 0;

      const estimatedCost = job.estimatedCost || 0;
      const costVariance = totalCost - estimatedCost;
      const costVariancePercent =
        estimatedCost > 0 ? (costVariance / estimatedCost) * 100 : 0;

      return {
        jobId: job.id,
        jobNumber: job.jobNumber,
        jobName: job.jobName,
        customerName: job.customerName,
        revenue,
        totalCost,
        grossProfit,
        grossProfitMargin,
        estimatedCost,
        costVariance,
        costVariancePercent,
        status: job.status,
        completedDate: job.completedDate,
      };
    });
  }

  get customerProfitabilityReport(): CustomerProfitability[] {
    // Group jobs by customer
    const customerMap = new Map<string, Job[]>();
    this.jobs.forEach((job) => {
      if (job.customerId) {
        if (!customerMap.has(job.customerId)) {
          customerMap.set(job.customerId, []);
        }
        customerMap.get(job.customerId)!.push(job);
      }
    });

    return Array.from(customerMap.entries()).map(([customerId, jobs]) => {
      const totalCost = jobs.reduce((sum, job) => sum + (job.actualCost || 0), 0);
      const totalRevenue = 0; // Placeholder - would sum invoices
      const grossProfit = totalRevenue - totalCost;
      const grossProfitMargin =
        totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;

      return {
        customerId,
        customerName: jobs[0].customerName || customerId,
        totalRevenue,
        totalCost,
        grossProfit,
        grossProfitMargin,
        jobCount: jobs.length,
        averageJobValue: jobs.length > 0 ? totalRevenue / jobs.length : 0,
        averageJobProfit: jobs.length > 0 ? grossProfit / jobs.length : 0,
      };
    });
  }

  get jobCostVarianceReport(): JobCostVariance[] {
    return this.jobs.map((job) => {
      const budgetedCost = job.estimatedCost || 0;
      const actualCost = job.actualCost || 0;
      const variance = actualCost - budgetedCost;
      const variancePercent =
        budgetedCost > 0 ? (variance / budgetedCost) * 100 : 0;

      // Get category variances from cost allocations
      const allocations = this.costAllocations.filter(
        (ca) => ca.jobId === job.id
      );
      const materialVariance =
        allocations
          .filter((ca) => ca.category === "material")
          .reduce((sum, ca) => sum + ca.totalCost, 0) -
        budgetedCost * 0.4; // Assume 40% material budget
      const laborVariance =
        allocations
          .filter((ca) => ca.category === "labor")
          .reduce((sum, ca) => sum + ca.totalCost, 0) -
        budgetedCost * 0.35; // Assume 35% labor budget
      const overheadVariance =
        allocations
          .filter((ca) => ca.category === "overhead")
          .reduce((sum, ca) => sum + ca.totalCost, 0) -
        budgetedCost * 0.15; // Assume 15% overhead budget
      const equipmentVariance =
        allocations
          .filter((ca) => ca.category === "equipment")
          .reduce((sum, ca) => sum + ca.totalCost, 0) -
        budgetedCost * 0.1; // Assume 10% equipment budget

      // Forecast to complete (for in-progress jobs)
      const percentComplete = job.actualHours
        ? job.actualHours / (job.budgetedHours || 1)
        : 0;
      const forecastToComplete =
        percentComplete > 0 && percentComplete < 1
          ? budgetedCost - actualCost / percentComplete
          : 0;

      const estimatedFinalCost =
        percentComplete > 0 && percentComplete < 1
          ? actualCost / percentComplete
          : actualCost;

      return {
        jobId: job.id,
        jobNumber: job.jobNumber,
        jobName: job.jobName,
        budgetedCost,
        actualCost,
        variance,
        variancePercent,
        materialVariance,
        laborVariance,
        overheadVariance,
        equipmentVariance,
        status: job.status,
        forecastToComplete,
        estimatedFinalCost,
      };
    });
  }

  // ============================================================================
  // AGING REPORTS
  // ============================================================================

  get arAgingReport(): ARAgingReport {
    const today = new Date();

    const customerAging = this.invoices.reduce((acc, invoice) => {
      if (invoice.amountDue <= 0) return acc;

      const dueDate = new Date(invoice.dueDate);
      const daysOverdue = Math.floor(
        (today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      const customerId = invoice.customerId.toString();
      if (!acc[customerId]) {
        acc[customerId] = {
          customerId,
          customerName: invoice.customerName || customerId,
          aging: {
            current: 0,
            days31to60: 0,
            days61to90: 0,
            over90: 0,
            total: 0,
          },
        };
      }

      const amount = invoice.amountDue;
      if (daysOverdue <= 30) {
        acc[customerId].aging.current += amount;
      } else if (daysOverdue <= 60) {
        acc[customerId].aging.days31to60 += amount;
      } else if (daysOverdue <= 90) {
        acc[customerId].aging.days61to90 += amount;
      } else {
        acc[customerId].aging.over90 += amount;
      }
      acc[customerId].aging.total += amount;

      return acc;
    }, {} as Record<string, { customerId: string; customerName: string; aging: AgingBucket }>);

    const customerAgingArray = Object.values(customerAging);

    const totalAging: AgingBucket = customerAgingArray.reduce(
      (acc, customer) => ({
        current: acc.current + customer.aging.current,
        days31to60: acc.days31to60 + customer.aging.days31to60,
        days61to90: acc.days61to90 + customer.aging.days61to90,
        over90: acc.over90 + customer.aging.over90,
        total: acc.total + customer.aging.total,
      }),
      { current: 0, days31to60: 0, days61to90: 0, over90: 0, total: 0 }
    );

    return {
      id: `ar-aging-${this.endDate}`,
      asOfDate: this.endDate,
      customerAging: customerAgingArray,
      totalAging,
    };
  }

  get apAgingReport(): APAgingReport {
    const today = new Date();

    const supplierAging = this.bills.reduce((acc, bill) => {
      const remainingAmount = bill.totalAmount; // Simplified
      if (remainingAmount <= 0) return acc;

      const dueDate = new Date(bill.dueDate);
      const daysOverdue = Math.floor(
        (today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      const supplierId = bill.supplierId;
      if (!acc[supplierId]) {
        acc[supplierId] = {
          supplierId,
          supplierName: bill.supplierName || supplierId,
          aging: {
            current: 0,
            days31to60: 0,
            days61to90: 0,
            over90: 0,
            total: 0,
          },
        };
      }

      if (daysOverdue <= 30) {
        acc[supplierId].aging.current += remainingAmount;
      } else if (daysOverdue <= 60) {
        acc[supplierId].aging.days31to60 += remainingAmount;
      } else if (daysOverdue <= 90) {
        acc[supplierId].aging.days61to90 += remainingAmount;
      } else {
        acc[supplierId].aging.over90 += remainingAmount;
      }
      acc[supplierId].aging.total += remainingAmount;

      return acc;
    }, {} as Record<string, { supplierId: string; supplierName: string; aging: AgingBucket }>);

    const supplierAgingArray = Object.values(supplierAging);

    const totalAging: AgingBucket = supplierAgingArray.reduce(
      (acc, supplier) => ({
        current: acc.current + supplier.aging.current,
        days31to60: acc.days31to60 + supplier.aging.days31to60,
        days61to90: acc.days61to90 + supplier.aging.days61to90,
        over90: acc.over90 + supplier.aging.over90,
        total: acc.total + supplier.aging.total,
      }),
      { current: 0, days31to60: 0, days61to90: 0, over90: 0, total: 0 }
    );

    return {
      id: `ap-aging-${this.endDate}`,
      asOfDate: this.endDate,
      supplierAging: supplierAgingArray,
      totalAging,
    };
  }

  // ============================================================================
  // CASH FLOW FORECAST
  // ============================================================================

  get cashFlowForecast(): CashFlowForecast {
    const forecastPeriod = 30; // 30 days
    const currentCash = this.balanceSheet.assets.currentAssets.cash;

    // Projected receipts from outstanding invoices
    const projectedReceipts = this.invoices
      .filter((inv) => inv.amountDue > 0)
      .map((inv) => ({
        date: inv.dueDate,
        amount: inv.amountDue,
        source: `Invoice ${inv.invoiceNumber}`,
      }));

    // Projected payments from outstanding bills
    const projectedPayments = this.bills
      .filter((bill) => bill.totalAmount > 0)
      .map((bill) => ({
        date: bill.dueDate,
        amount: bill.totalAmount,
        payee: bill.supplierName,
      }));

    // Daily projections
    const dailyProjections: CashFlowForecast["dailyProjections"] = [];
    let runningBalance = currentCash;
    let minimumCash = currentCash;
    let minimumCashDate = this.endDate;

    for (let i = 0; i < forecastPeriod; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split("T")[0];

      const receipts = projectedReceipts
        .filter((r) => r.date === dateStr)
        .reduce((sum, r) => sum + r.amount, 0);

      const payments = projectedPayments
        .filter((p) => p.date === dateStr)
        .reduce((sum, p) => sum + p.amount, 0);

      const netCash = receipts - payments;
      runningBalance += netCash;

      if (runningBalance < minimumCash) {
        minimumCash = runningBalance;
        minimumCashDate = dateStr;
      }

      dailyProjections.push({
        date: dateStr,
        receipts,
        payments,
        netCash,
        runningBalance,
      });
    }

    return {
      id: `cash-forecast-${this.endDate}`,
      forecastDate: this.endDate,
      forecastPeriod,
      currentCash,
      projectedReceipts,
      projectedPayments,
      dailyProjections,
      minimumCash,
      minimumCashDate,
    };
  }

  // ============================================================================
  // EXECUTIVE KPI DASHBOARD
  // ============================================================================

  get executiveKPIs(): ExecutiveKPIs {
    const income = this.incomeStatement;
    const balance = this.balanceSheet;
    const cashFlow = this.cashFlowStatement;
    const arAging = this.arAgingReport;
    const apAging = this.apAgingReport;

    // Calculate prior period for growth rates (simplified)
    const revenueGrowth = 0; // Placeholder - would compare to prior period
    const cashGrowth = 0; // Placeholder

    // Calculate EBITDA
    const ebitda = income.operatingIncome + income.operatingExpenses.depreciation;
    const ebitdaMargin = income.revenue.totalRevenue > 0 ? (ebitda / income.revenue.totalRevenue) * 100 : 0;

    // Operational metrics
    const jobsCompleted = this.jobs.filter((j) => j.status === "completed").length;
    const jobsInProgress = this.jobs.filter((j) => j.status === "in_progress").length;

    const completedJobs = this.jobs.filter((j) => j.completedDate && j.startDate);
    const averageJobDuration =
      completedJobs.length > 0
        ? completedJobs.reduce((sum, job) => {
            const start = new Date(job.startDate!);
            const end = new Date(job.completedDate!);
            return sum + (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
          }, 0) / completedJobs.length
        : 0;

    const onTimeDeliveryRate = 0; // Placeholder
    const productionEfficiency = 0; // Placeholder

    // Days Sales Outstanding (DSO)
    const averageDailyRevenue = income.revenue.totalRevenue / 30; // Simplified
    const dso = averageDailyRevenue > 0 ? balance.assets.currentAssets.accountsReceivable / averageDailyRevenue : 0;

    // Days Payables Outstanding (DPO)
    const averageDailyCOGS = income.costOfGoodsSold.totalCOGS / 30; // Simplified
    const dpo = averageDailyCOGS > 0 ? balance.liabilities.currentLiabilities.accountsPayable / averageDailyCOGS : 0;

    return {
      period: this.selectedPeriod,
      startDate: this.startDate,
      endDate: this.endDate,
      financial: {
        revenue: income.revenue.totalRevenue,
        revenueGrowth,
        grossProfit: income.grossProfit,
        grossProfitMargin: income.grossProfitMargin,
        netIncome: income.netIncome,
        netProfitMargin: income.netProfitMargin,
        ebitda,
        ebitdaMargin,
      },
      operational: {
        jobsCompleted,
        jobsInProgress,
        averageJobDuration,
        onTimeDeliveryRate,
        productionEfficiency,
      },
      cashFlow: {
        currentCash: balance.assets.currentAssets.cash,
        cashGrowth,
        daysOfCash: averageDailyCOGS > 0 ? balance.assets.currentAssets.cash / averageDailyCOGS : 0,
        operatingCashFlow: cashFlow.operatingActivities.netCashFromOperations,
      },
      receivables: {
        totalAR: arAging.totalAging.total,
        overdueAR: arAging.totalAging.days31to60 + arAging.totalAging.days61to90 + arAging.totalAging.over90,
        dso,
        collectionEfficiency: arAging.totalAging.total > 0 ? (arAging.totalAging.current / arAging.totalAging.total) * 100 : 0,
      },
      payables: {
        totalAP: apAging.totalAging.total,
        overdueAP: apAging.totalAging.days31to60 + apAging.totalAging.days61to90 + apAging.totalAging.over90,
        dpo,
      },
    };
  }

  get isLoading(): boolean {
    return (
      this.invoicesStore?.isServicePending ||
      this.billsStore?.isServicePending ||
      this.paymentsStore?.isServicePending ||
      this.jobStore?.isServicePending ||
      this.costAllocationsStore?.isServicePending ||
      this.productionCostsStore?.isServicePending ||
      this.laborEntriesStore?.isServicePending ||
      this.equipmentCostsStore?.isServicePending ||
      false
    );
  }

  // ============================================================================
  // PLANT SUBTOTALS
  // ============================================================================

  /**
   * Plant subtotals for Income Statement
   * Returns revenue, COGS, and profit broken down by plant
   */
  get incomeStatementByPlant(): Record<string, {
    plantId: string;
    plantName: string;
    plantCode: string;
    revenue: number;
    cogs: number;
    grossProfit: number;
    operatingExpenses: number;
    netIncome: number;
  }> {
    const result: Record<string, {
      plantId: string;
      plantName: string;
      plantCode: string;
      revenue: number;
      cogs: number;
      grossProfit: number;
      operatingExpenses: number;
      netIncome: number;
    }> = {};

    // Mock data - in real implementation, would filter actual invoices/bills by plant
    const plants = this.effectivePlants;
    const totalIncome = this.incomeStatement;

    // Distribute proportionally for demo (would use actual plant-tagged data)
    const plantDistribution: Record<string, number> = {
      "plant-1": 0.35, // Apex Main - 35%
      "plant-2": 0.25, // Northpoint - 25%
      "plant-3": 0.15, // Sierra Valley - 15%
      "plant-4": 0.15, // Summit - 15%
      "plant-5": 0.10, // Harbor - 10%
    };

    plants.forEach((plantId) => {
      const distribution = plantDistribution[plantId] || 0.2;
      const revenue = totalIncome.revenue.totalRevenue * distribution;
      const cogs = totalIncome.costOfGoodsSold.totalCOGS * distribution;
      const grossProfit = revenue - cogs;
      const operatingExpenses = totalIncome.operatingExpenses.totalOperatingExpenses * distribution;
      const netIncome = grossProfit - operatingExpenses;

      result[plantId] = {
        plantId,
        plantName: this.getPlantName(plantId),
        plantCode: this.getPlantCode(plantId),
        revenue,
        cogs,
        grossProfit,
        operatingExpenses,
        netIncome,
      };
    });

    return result;
  }

  /**
   * AR Aging by Plant
   */
  get arAgingByPlant(): Record<string, {
    plantId: string;
    plantName: string;
    current: number;
    days31to60: number;
    days61to90: number;
    over90: number;
    total: number;
  }> {
    const result: Record<string, {
      plantId: string;
      plantName: string;
      current: number;
      days31to60: number;
      days61to90: number;
      over90: number;
      total: number;
    }> = {};

    const plants = this.effectivePlants;
    const totalAging = this.arAgingReport.totalAging;

    // Mock distribution - would use actual plant-tagged invoices
    const plantDistribution: Record<string, number> = {
      "plant-1": 0.35,
      "plant-2": 0.25,
      "plant-3": 0.15,
      "plant-4": 0.15,
      "plant-5": 0.10,
    };

    plants.forEach((plantId) => {
      const distribution = plantDistribution[plantId] || 0.2;

      result[plantId] = {
        plantId,
        plantName: this.getPlantName(plantId),
        current: totalAging.current * distribution,
        days31to60: totalAging.days31to60 * distribution,
        days61to90: totalAging.days61to90 * distribution,
        over90: totalAging.over90 * distribution,
        total: totalAging.total * distribution,
      };
    });

    return result;
  }

  /**
   * AP Aging by Plant
   */
  get apAgingByPlant(): Record<string, {
    plantId: string;
    plantName: string;
    current: number;
    days31to60: number;
    days61to90: number;
    over90: number;
    total: number;
  }> {
    const result: Record<string, {
      plantId: string;
      plantName: string;
      current: number;
      days31to60: number;
      days61to90: number;
      over90: number;
      total: number;
    }> = {};

    const plants = this.effectivePlants;
    const totalAging = this.apAgingReport.totalAging;

    // Mock distribution - would use actual plant-tagged bills
    const plantDistribution: Record<string, number> = {
      "plant-1": 0.35,
      "plant-2": 0.25,
      "plant-3": 0.15,
      "plant-4": 0.15,
      "plant-5": 0.10,
    };

    plants.forEach((plantId) => {
      const distribution = plantDistribution[plantId] || 0.2;

      result[plantId] = {
        plantId,
        plantName: this.getPlantName(plantId),
        current: totalAging.current * distribution,
        days31to60: totalAging.days31to60 * distribution,
        days61to90: totalAging.days61to90 * distribution,
        over90: totalAging.over90 * distribution,
        total: totalAging.total * distribution,
      };
    });

    return result;
  }

  /**
   * Get plant subtotals summary for current report
   */
  get plantSubtotalsSummary(): Array<{
    plantId: string;
    plantName: string;
    plantCode: string;
    revenue: number;
    expenses: number;
    netIncome: number;
    arTotal: number;
    apTotal: number;
  }> {
    return this.effectivePlants.map((plantId) => {
      const income = this.incomeStatementByPlant[plantId];
      const ar = this.arAgingByPlant[plantId];
      const ap = this.apAgingByPlant[plantId];

      return {
        plantId,
        plantName: income?.plantName || this.getPlantName(plantId),
        plantCode: income?.plantCode || this.getPlantCode(plantId),
        revenue: income?.revenue || 0,
        expenses: (income?.cogs || 0) + (income?.operatingExpenses || 0),
        netIncome: income?.netIncome || 0,
        arTotal: ar?.total || 0,
        apTotal: ap?.total || 0,
      };
    });
  }

  /**
   * Report configuration for export
   */
  get reportConfig() {
    return {
      showPlantColumn: this.showPlantColumn,
      subtotalByPlant: this.subtotalByPlant,
      selectedPlants: this.selectedPlants,
      effectivePlants: this.effectivePlants,
      availablePlants: this.availablePlants,
    };
  }
}

export default FinancialReportsStore;
